export const RENDERER_DEFAULTS = {
  toneMappingExposure: 1,
};

export const ORTHOGRAPHIC_CAMERA_DEFAULTS = {
  position: {
    x: 4,
    y: 19,
    z: 20,
  },
  zoom: 20,
};

export const ORBIT_CONTROLS_DEFAULTS = {
  enableDamping: true,
  dampingFactor: 0.65,
  enableZoom: true,
  rotateSpeed: 0.8,
  zoomSpeed: 0.9,
  keyPanSpeed: 0.7,
  enablePan: true,
  maxPolarAngle: (Math.PI / 2) - 0.01,
};

export const SHOP_PREVIEW_SCENE_DEFAULTS = {
  controls: {
    ...ORBIT_CONTROLS_DEFAULTS,
    minZoom: 10,
    maxZoom: 120,
  },
  lights: {
    ambient: {
      color: 0x222222,
      intensity: 2.7,
    },
    directional: [
      {
        color: 0xffffff,
        intensity: 1.6,
        position: { x: 5, y: -5, z: 0.5 },
      },
      {
        color: 0xffffff,
        intensity: 1.6,
        position: { x: 1, y: 5, z: 1 },
      },
      {
        color: 0xffffff,
        intensity: 1.4,
        position: { x: -3, y: 5, z: -3 },
      },
    ],
    fixedSpot: {
      color: 0xffffff,
      intensity: 1.7,
      position: { x: -34, y: 2, z: 10 },
      target: { x: 0, y: 6, z: -24 },
    },
    meshSpot: {
      color: 0xffffff,
      intensity: 1.7,
      position: { x: 4, y: 19, z: -40 },
    },
  },
  skybox: {
    y: 48.5,
  },
};

export const ADMIN_CSG_SCENE_DEFAULTS = {
  controls: {
    ...ORBIT_CONTROLS_DEFAULTS,
    minZoom: 10,
    maxZoom: 190,
  },
  lights: {
    ambient: {
      color: 0x222222,
      intensity: 2.7,
    },
    directional: [
      {
        color: 0xffffff,
        intensity: 1.2,
        position: { x: 5, y: -5, z: 0.5 },
      },
      {
        color: 0xffffff,
        intensity: 1.3,
        position: { x: 1, y: 5, z: 1 },
      },
      {
        color: 0xffffff,
        intensity: 1.3,
        position: { x: -3, y: 5, z: -3 },
      },
    ],
    fixedSpot: {
      color: 0xffffff,
      intensity: 1.5,
      position: { x: -34, y: 2, z: 10 },
      target: { x: 0, y: 6, z: -24 },
    },
  },
  backgroundColor: 0x000000,
  axisHelperSize: 5,
};

export const applyRendererDefaults = (THREE, renderer) => {
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = RENDERER_DEFAULTS.toneMappingExposure;
};

export const applyCameraDefaults = (camera, config = ORTHOGRAPHIC_CAMERA_DEFAULTS) => {
  camera.position.set(config.position.x, config.position.y, config.position.z);
  camera.zoom = config.zoom;
  camera.updateProjectionMatrix();
};

export const applyOrbitControlsDefaults = (orbitControls, config) => {
  orbitControls.enableDamping = config.enableDamping;
  orbitControls.dampingFactor = config.dampingFactor;
  orbitControls.enableZoom = config.enableZoom;
  orbitControls.rotateSpeed = config.rotateSpeed;
  orbitControls.zoomSpeed = config.zoomSpeed;
  orbitControls.keyPanSpeed = config.keyPanSpeed;
  orbitControls.enablePan = config.enablePan;
  orbitControls.maxPolarAngle = config.maxPolarAngle;
  orbitControls.minZoom = config.minZoom;
  orbitControls.maxZoom = config.maxZoom;
};

export const addSceneLights = (THREE, scene, lightingConfig) => {
  const ambientLight = new THREE.AmbientLight(
    lightingConfig.ambient.color,
    lightingConfig.ambient.intensity
  );
  scene.add(ambientLight);

  const directionalLights = lightingConfig.directional.map((config) => {
    const light = new THREE.DirectionalLight(config.color, config.intensity);
    light.position.set(config.position.x, config.position.y, config.position.z);
    scene.add(light);
    return light;
  });

  let fixedSpotLight = null;

  if (lightingConfig.fixedSpot) {
    fixedSpotLight = new THREE.SpotLight(
      lightingConfig.fixedSpot.color,
      lightingConfig.fixedSpot.intensity
    );
    fixedSpotLight.position.set(
      lightingConfig.fixedSpot.position.x,
      lightingConfig.fixedSpot.position.y,
      lightingConfig.fixedSpot.position.z
    );
    fixedSpotLight.target.position.set(
      lightingConfig.fixedSpot.target.x,
      lightingConfig.fixedSpot.target.y,
      lightingConfig.fixedSpot.target.z
    );
    scene.add(fixedSpotLight);
  }

  return {
    ambientLight,
    directionalLights,
    fixedSpotLight,
  };
};

export const createMeshSpotLight = (THREE, config, targetPosition) => {
  const spotLight = new THREE.SpotLight(config.color, config.intensity);
  spotLight.position.set(config.position.x, config.position.y, config.position.z);
  spotLight.target.position.copy(targetPosition);
  return spotLight;
};
